<script setup lang="ts">
import type { Celebration } from "../../domain/celebrate.js";

/**
 * Celebration overlay (PRD 6.4): brief, centred, and non-blocking.
 *
 * `pointer-events: none` is the whole contract -- the next player must be able
 * to start entering their score straight through it. Nothing here waits for a
 * dismissal, and the animation is disabled under prefers-reduced-motion by the
 * global rule in tokens.css.
 */

defineProps<{ celebration: Celebration | null }>();
</script>

<template>
  <Transition name="pop">
    <div
      v-if="celebration"
      class="wrap"
      :class="`wrap--${celebration.tone}`"
      role="status"
      aria-live="polite"
    >
      <div class="card">
        <div
          class="icon"
          aria-hidden="true"
        >
          {{ celebration.icon }}
        </div>
        <div class="title">
          {{ celebration.title }}
        </div>
        <div class="detail">
          {{ celebration.detail }}
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.wrap {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Non-blocking: taps go straight through to the keypad behind it. */
  pointer-events: none;
  z-index: 50;
}

.card {
  padding: 18px 26px;
  border-radius: 20px;
  text-align: center;
  color: #fff;
  background: var(--accent);
  box-shadow: 0 12px 40px rgb(0 0 0 / 0.28);
}

.wrap--bad .card {
  background: var(--danger);
}

.icon {
  font-size: 44px;
  line-height: 1;
  margin-bottom: 2px;
}

.title {
  font-family: var(--font-num);
  font-variant-numeric: tabular-nums;
  font-size: 34px;
  font-weight: 800;
  line-height: 1.1;
}

.detail {
  font-size: 13px;
  opacity: 0.9;
  margin-top: 2px;
}

.pop-enter-active {
  transition:
    opacity 0.16s ease,
    transform 0.22s cubic-bezier(0.2, 1.4, 0.4, 1);
}

.pop-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.pop-enter-from {
  opacity: 0;
  transform: scale(0.7);
}

.pop-leave-to {
  opacity: 0;
  transform: scale(1.06) translateY(-8px);
}
</style>
